import {
  BOOKMARK_POSITION,
  NovelBookmark,
  type Collection,
  type NovelBookmarkEntity,
} from "~/src/api";
import {
  useInfiniteQuery,
  useMutation,
  type QueryClient,
  type QueryKey,
} from "@tanstack/vue-query";
import cloneDeep from "clone-deep";

type InfQueryDataType = {
  pages: Collection<NovelBookmarkEntity>[];
  pageParams: number[];
};

export const useNovelBookmarkManager = (
  queryClient: QueryClient,
  titleId: MaybeRefOrGetter<string>,
  chapterId?: MaybeRefOrGetter<string | undefined>,
) => {
  const queryKey = ["novel_reader_bookmarks", titleId, chapterId];

  const bookmarkQuery = useInfiniteQuery(
    {
      queryKey: queryKey,
      queryFn: async ({ pageParam, queryKey }) => {
        const [_, titleId, chapterId] = extractQueryKey(queryKey);
        if (!titleId) throw new Error("Title id required for bookmark manager");

        const resp = await NovelBookmark.find(
          {
            titleId: titleId,
            novelChapterId: chapterId,
            offset: pageParam,
            order: {
              natural: "desc",
            },
            includes: ["novel_chapter", "novel_chapter_paragraph"],
            showSelections: false,
          },
          await getTokenOrThrow(),
        );

        return resp;
      },
      initialPageParam: 0,
      getNextPageParam: (prev, pages, pageParam) => {
        if (prev.meta.total > prev.meta.offset + prev.meta.limit)
          return prev.meta.offset + prev.meta.limit;
        return null;
      },
      select: (data) => ({
        type: "collection",
        data: data.pages.flatMap((page) => page.data),
        meta: data.pages[0].meta,
      }),
      staleTime: 1000 * 60 * 5,
    },
    queryClient,
  );

  const createMutation = useMutation({
    mutationFn: async (params: {
      chapterId: string;
      paragraphId: string;
      noRefresh?: boolean;
    }) => {
      const id = toValue(titleId);
      if (!id) return;

      return await NovelBookmark.create(
        {
          position: BOOKMARK_POSITION,
          novelChapterId: params.chapterId,
          novelChapterParagraphId: params.paragraphId,
        },
        await getTokenOrThrow(),
      );
    },
    onMutate: async (params: {
      chapterId: string;
      paragraphId: string;
      noRefresh?: boolean;
    }) => {
      const previousDataForChapterQuery = await addBookmarkToQueryData(
        queryClient,
        ["novel_reader_bookmarks", toValue(titleId), params.chapterId],
        params,
      );
      const previousDataForGenericQuery = await addBookmarkToQueryData(
        queryClient,
        ["novel_reader_bookmarks", toValue(titleId), undefined],
        params,
      );

      return { previousDataForChapterQuery, previousDataForGenericQuery };
    },
    onError: (err, variables, context) => {
      if (context?.previousDataForChapterQuery) {
        queryClient.setQueryData(
          ["novel_reader_bookmarks", toValue(titleId), variables.chapterId],
          context.previousDataForChapterQuery,
        );
      }

      if (context?.previousDataForGenericQuery) {
        queryClient.setQueryData(
          ["novel_reader_bookmarks", toValue(titleId), undefined],
          context.previousDataForGenericQuery,
        );
      }
    },
    onSettled: (data, err, variables) => {
      // if (!variables.noRefresh)
      queryClient.invalidateQueries({
        queryKey: [
          "novel_reader_bookmarks",
          toValue(titleId),
          variables.chapterId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["novel_reader_bookmarks", toValue(titleId), undefined],
      });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: async (params: {
      chapterId: string;
      bookmarkId: string;
      paragraphId: string;
      noRefresh?: boolean;
    }) => NovelBookmark.delete(params.bookmarkId, 0, await getTokenOrThrow()),
    onMutate: async (params) => {
      const previousDataForChapterQuery = await removeBookmarkFromQueryData(
        queryClient,
        ["novel_reader_bookmarks", toValue(titleId), params.chapterId],
        params,
      );

      const previousDataForGenericQuery = await removeBookmarkFromQueryData(
        queryClient,
        ["novel_reader_bookmarks", toValue(titleId), undefined],
        params,
      );

      return { previousDataForChapterQuery, previousDataForGenericQuery };
    },
    onError: (err, variables, context) => {
      if (context?.previousDataForChapterQuery) {
        queryClient.setQueryData(
          ["novel_reader_bookmarks", toValue(titleId), variables.chapterId],
          context.previousDataForChapterQuery,
        );
      }

      if (context?.previousDataForGenericQuery) {
        queryClient.setQueryData(
          ["novel_reader_bookmarks", toValue(titleId), undefined],
          context.previousDataForGenericQuery,
        );
      }
    },
    onSettled: (data, err, variables) => {
      if (!variables.noRefresh) {
        queryClient.invalidateQueries({
          queryKey: [
            "novel_reader_bookmarks",
            toValue(titleId),
            variables.chapterId,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: ["novel_reader_bookmarks", toValue(titleId), undefined],
        });
      }
    },
  });
  const editMutation = useMutation({
    mutationFn: async (params: {
      chapterId: string;
      bookmarkId: string;
      comment?: string;
    }) => {
      return NovelBookmark.update(
        params.bookmarkId,
        { comment: params.comment },
        await getTokenOrThrow(),
      );
    },
    onMutate: async (params) => {
      const previousDataForChapterQuery = await updateBookmarkOnQueryData(
        queryClient,
        ["novel_reader_bookmarks", toValue(titleId), params.chapterId],
        params,
      );

      const previousDataForGenericQuery = await updateBookmarkOnQueryData(
        queryClient,
        ["novel_reader_bookmarks", toValue(titleId), undefined],
        params,
      );

      return { previousDataForChapterQuery, previousDataForGenericQuery };
    },
    onError: (err, variables, context) => {
      if (context?.previousDataForChapterQuery) {
        queryClient.setQueryData(
          ["novel_reader_bookmarks", toValue(titleId), variables.chapterId],
          context.previousDataForChapterQuery,
        );
      }

      if (context?.previousDataForGenericQuery) {
        queryClient.setQueryData(
          ["novel_reader_bookmarks", toValue(titleId), undefined],
          context.previousDataForGenericQuery,
        );
      }
    },
    onSettled: (data, err, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "novel_reader_bookmarks",
          toValue(titleId),
          variables.chapterId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["novel_reader_bookmarks", toValue(titleId), undefined],
      });
    },
  });

  return {
    bookmarkQuery,
    createMutation,
    deleteMutation,
    editMutation,
    invalidate: () =>
      queryClient.invalidateQueries({ queryKey: toValue(queryKey) }),
  };
};

async function addBookmarkToQueryData(
  client: QueryClient,
  queryKey: QueryKey,
  params: {
    chapterId: string;
    paragraphId: string;
  },
) {
  await client.cancelQueries({ queryKey });
  const previousData = client.getQueryData(queryKey);

  client.setQueryData(queryKey, (oldValue?: InfQueryDataType) => {
    const base: InfQueryDataType = oldValue
      ? cloneDeep(oldValue)
      : {
          pages: [
            {
              type: "collection",
              data: [],
              meta: {
                total: 0,
                offset: 0,
                limit: 50,
              },
            },
          ],
          pageParams: [0],
        };

    base.pages[0].data.push({
      id: `placeholder_${params.paragraphId}`,
      type: "novel_chapter_bookmark",
      attributes: {
        comment: null,
        position: BOOKMARK_POSITION,
        createdAt: new Date().toISOString(),
      },
      relationships: [
        {
          type: "novel_chapter_paragraph",
          id: params.paragraphId,
        },
        {
          type: "novel_chapter",
          id: params.chapterId,
        },
      ],
    });
    base.pages.forEach((page) => (page.meta.total += 1));
    return base;
  });

  return previousData;
}

async function updateBookmarkOnQueryData(
  client: QueryClient,
  queryKey: QueryKey,
  params: {
    chapterId: string;
    bookmarkId: string;
    comment?: string;
  },
) {
  await client.cancelQueries({ queryKey });
  const previousData = client.getQueryData(queryKey);

  client.setQueryData(queryKey, (oldValue?: InfQueryDataType) => {
    if (!oldValue) return undefined;

    oldValue.pages.forEach((page) => {
      const index = page.data.findIndex((b) => b.id === params.bookmarkId);
      if (index >= 0) {
        page.data[index].attributes.comment = params.comment ?? null;
      }
    });
  });

  return previousData;
}

async function removeBookmarkFromQueryData(
  client: QueryClient,
  queryKey: QueryKey,
  params: {
    chapterId: string;
    bookmarkId: string;
    paragraphId: string;
  },
) {
  await client.cancelQueries({ queryKey });
  const previousData = client.getQueryData(queryKey);

  client.setQueryData(queryKey, (oldValue?: InfQueryDataType) => {
    if (!oldValue) return undefined;
    const base = cloneDeep(oldValue);
    base.pages.forEach((page) => {
      const index = page.data.findIndex(
        (bm) =>
          bm.id === params.bookmarkId ||
          bm.id === `placeholder${params.paragraphId}`,
      );
      if (index >= 0) {
        page.data.splice(index, 1);
        page.meta.total -= 1;
      }
    });
    return base;
  });

  return previousData;
}
